import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import { shipmentAPI } from "../services/api";
import type { PartnerSuggestion, ShipmentFilters } from "../types";
import { vehicleRefLabel } from "../utils/vehicleLabel";

interface UseShipmentExportProps {
  filters: ShipmentFilters;
  selectedStatuses: string[];
  selectedDirections: string[];
  selectedPartners: PartnerSuggestion[];
  selectedAddresses: PartnerSuggestion[];
  truckCarrier: string[];
  trailerCarrier: string[];
  dateRangeStart: string;
  dateRangeEnd: string;
  pickupLocation: string;
  deliveryLocation: string;
  isServiceIncluded?: boolean;
}

export const useShipmentExport = ({
  filters,
  selectedStatuses,
  selectedDirections,
  selectedPartners,
  selectedAddresses,
  truckCarrier,
  trailerCarrier,
  dateRangeStart,
  dateRangeEnd,
  pickupLocation,
  deliveryLocation,
  isServiceIncluded = true,
}: UseShipmentExportProps) => {
  const handleExportReport = async () => {
    try {
      const params: any = {
        page: 1,
        limit: 0,
      };

      if (filters.search) params.search = filters.search;
      if (selectedStatuses.length > 0) params.statuses = selectedStatuses;
      if (selectedDirections.length > 0) params.directions = selectedDirections;
      if (dateRangeStart) params.dateFrom = dateRangeStart;
      if (dateRangeEnd) params.dateTo = dateRangeEnd;
      if (pickupLocation.trim()) params.pickupLocation = pickupLocation.trim();
      if (deliveryLocation.trim())
        params.deliveryLocation = deliveryLocation.trim();

      if (selectedPartners.length > 0) {
        params.companies = selectedPartners.map((c) => c.companyName.trim());
      }
      if (selectedAddresses.length > 0) {
        params.companies = [
          ...(params.companies || []),
          ...selectedAddresses.map((c) => c.companyName.trim()),
        ];
      }
      if (truckCarrier.length > 0)
        params.vehicles = [
          ...new Set([...(params.vehicles || []), ...truckCarrier]),
        ];
      if (trailerCarrier.length > 0)
        params.vehicles = [
          ...new Set([...(params.vehicles || []), ...trailerCarrier]),
        ];

      // Default to last 30 days only when absolutely no filters are active
      const hasAnyFilter =
        selectedStatuses.length > 0 ||
        selectedDirections.length > 0 ||
        selectedPartners.length > 0 ||
        selectedAddresses.length > 0 ||
        truckCarrier.length > 0 ||
        trailerCarrier.length > 0 ||
        dateRangeStart ||
        dateRangeEnd ||
        pickupLocation.trim() ||
        deliveryLocation.trim();

      if (!hasAnyFilter) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        params.dateFrom = thirtyDaysAgo.toISOString().split("T")[0];
      }

      const response = await shipmentAPI.getShipments(params);
      const dataToExport = response.data.data.shipments;

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Shipments");

      const baseColumns = [
        { header: "Truck", key: "truck", width: 15 },
        { header: "Trailer", key: "trailer", width: 15 },
        { header: "Driver", key: "driver", width: 20 },
        { header: "ID", key: "shipmentNumber", width: 15 },
        { header: "Status", key: "status", width: 15 },
        { header: "Load Type", key: "loadType", width: 15 },
        { header: "Shipment Type", key: "shipmentType", width: 20 },
        { header: "In/Out", key: "direction", width: 15 },
      ];

      const serviceColumns = [
        { header: "Service Code", key: "serviceCode", width: 20 },
        { header: "Unit Type", key: "unitType", width: 15 },
        { header: "Quantity", key: "quantity", width: 10 },
        { header: "Price", key: "price", width: 10 },
        { header: "Total", key: "total", width: 12 },
      ];

      const tailColumns = [
        { header: "Pickup", key: "pickup", width: 25 },
        { header: "Pickup Date", key: "pickupDate", width: 15 },
        { header: "Delivery", key: "delivery", width: 25 },
        { header: "Delivery Date", key: "deliveryDate", width: 15 },
        { header: "Reference Nr", key: "referenceNumber", width: 20 },
        { header: "Carrier", key: "carrier", width: 25 },
        { header: "Client", key: "client", width: 25 },
        { header: "Container", key: "container", width: 20 },
      ];

      worksheet.columns = [
        ...baseColumns,
        ...(isServiceIncluded ? serviceColumns : []),
        ...tailColumns,
      ];

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 12, name: "Aptos Narrow" };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9D9D9" },
        };
        cell.border = {
          top: { style: "thick", color: { argb: "000000" } },
          left: { style: "thick", color: { argb: "000000" } },
          bottom: { style: "thick", color: { argb: "000000" } },
          right: { style: "thick", color: { argb: "000000" } },
        };
        cell.alignment = { vertical: "bottom", horizontal: "left" };
      });
      worksheet.getRow(1).height = 51;

      dataToExport.forEach((shipment: any) => {
        const pickupDateStr = shipment.pickup?.date
          ? shipment.pickup.date.split("T")[0]
          : null;
        const deliveryDateStr = shipment.delivery?.date
          ? shipment.delivery.date.split("T")[0]
          : null;

        const pickupDate = pickupDateStr ? new Date(pickupDateStr) : null;
        const deliveryDate = deliveryDateStr ? new Date(deliveryDateStr) : null;

        const safeValue = (value: any, fallback: string = "-") => {
          if (value === undefined || value === null || value === "")
            return fallback;
          return value;
        };

        // The truck as the shipment list shows it — its display name, else its plate.
        const extractPlate = (value: any) => {
          if (!value) return "";
          return vehicleRefLabel(value) || "-";
        };

        const extractDriver = (value: any) => {
          if (!value) return "";
          if (typeof value === "object")
            return `${value.firstName} ${value.lastName}` || "-";
          return value;
        };

        const services =
          Array.isArray(shipment.services) && shipment.services.length > 0
            ? shipment.services
            : [{}];

        if (!isServiceIncluded) {
          worksheet.addRow({
            truck: safeValue(extractPlate(shipment.carrier?.truckLicensePlate)),
            trailer: safeValue(
              extractPlate(shipment.carrier?.trailerLicensePlate),
            ),
            driver: safeValue(extractDriver(shipment.assignedDriver)),
            shipmentNumber: safeValue(shipment.shipmentNumber),
            status: safeValue(shipment.status),
            loadType: safeValue(shipment.loadType),
            shipmentType: safeValue(shipment.type),
            direction: safeValue(shipment.direction),
            pickup: safeValue(shipment.pickup?.companyName),
            pickupDate,
            delivery: safeValue(shipment.delivery?.companyName),
            deliveryDate,
            referenceNumber: safeValue(shipment.referenceNumber),
            carrier: safeValue(shipment.carrier?.companyName),
            client: safeValue(shipment.client?.companyName),
            container: safeValue(shipment.carrier?.containerNumber),
          });
          return;
        }

        services.forEach((service: any, index: number) => {
          worksheet.addRow({
            truck:
              index === 0
                ? safeValue(extractPlate(shipment.carrier?.truckLicensePlate))
                : "",
            trailer:
              index === 0
                ? safeValue(extractPlate(shipment.carrier?.trailerLicensePlate))
                : "",
            driver:
              index === 0
                ? safeValue(extractDriver(shipment.assignedDriver))
                : "",
            shipmentNumber:
              index === 0 ? safeValue(shipment.shipmentNumber) : "",
            status: index === 0 ? safeValue(shipment.status) : "",
            loadType: index === 0 ? safeValue(shipment.loadType) : "",
            shipmentType: index === 0 ? safeValue(shipment.type) : "",
            direction: index === 0 ? safeValue(shipment.direction) : "",
            serviceCode: service?.name || "",
            unitType: service?.type || "",
            quantity: service?.qty ?? "",
            price: service?.price ?? "",
            total: service?.total ?? "",
            pickup: index === 0 ? safeValue(shipment.pickup?.companyName) : "",
            pickupDate: index === 0 ? pickupDate : "",
            delivery:
              index === 0 ? safeValue(shipment.delivery?.companyName) : "",
            deliveryDate: index === 0 ? deliveryDate : "",
            referenceNumber:
              index === 0 ? safeValue(shipment.referenceNumber) : "",
            carrier:
              index === 0 ? safeValue(shipment.carrier?.companyName) : "",
            client: index === 0 ? safeValue(shipment.client?.companyName) : "",
            container:
              index === 0 ? safeValue(shipment.carrier?.containerNumber) : "",
          });
        });
      });

      worksheet.getColumn("pickupDate").numFmt = "m/d/yyyy";
      worksheet.getColumn("deliveryDate").numFmt = "m/d/yyyy";

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const timestamp = new Date().toISOString().split("T")[0];
      let filename = `shipments-report-${timestamp}`;
      filename += hasAnyFilter ? "-filtered" : "-last30days";
      filename += ".xlsx";

      link.href = url;
      link.download = filename;
      link.click();

      toast.success(
        `Report exported successfully (${dataToExport.length} shipments)`,
      );
    } catch (error) {
      toast.error("Failed to export report");
      console.error("Export error:", error);
    }
  };

  return { handleExportReport };
};
